// Pure helpers for the storage management (explorer uploads, folders, links — integration contract §9).
// No React / Redux / HTTP here. The server validates everything again: these rules only give early feedback.
import { getExtension, getRegisterKind, AZURE_MAX_FILE_SIZE_BYTES } from './AzureFileUpload.Functions';
import { getParentPath, getPathName, normalizeStoragePath } from './StorageBrowser.Functions';
import { AzureFileListLoaderKind, AzureFileTarget } from '../models/Entities/AzureFiles/AzureFileUpload';
import { AzureFileEntityTypeEnum, AzureFileTypeEnum, StorageOwnerTypeEnum } from '../models/Enums/AzureFileTypeEnum';

/** Extensions Contracts never accepts in any folder (contract §9.2). */
export const STORAGE_BLOCKED_EXTENSIONS = [
  'exe',
  'dll',
  'bat',
  'cmd',
  'com',
  'msi',
  'ps1',
  'sh',
  'js',
  'vbs',
  'jar',
  'scr',
  'html',
  'htm',
  'svg',
];

/** Contracts FileTypeEnum names: system folders (contract §9.1). */
export const STORAGE_FILE_TYPE_FOLDER_NAMES = [
  'ProviderSettlement',
  'SponsorPaymentProposal',
  'ProtocolContract',
  'ProviderInvoice',
  'ProviderServicesPerformed',
  'SettlementInvoice',
  'ServiceReceipt',
  'PractitionerInvoice',
  'PaymentReceipt',
];

export const STORAGE_FOLDER_NAME_MAX_LENGTH = 100;

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/**
 * Mirror of contract §9.1: owner folders (sponsor_/practitioner_/proveedor_/obrasocial_ + id),
 * protocolo_{int>0}, FileTypeEnum names and anything starting with entidad_ (all case-insensitive).
 */
export const isSystemFolderName = (name: string | null | undefined): boolean => {
  const value = (name ?? '').trim();
  if (!value) return false;
  if (/^(sponsor|practitioner|proveedor|obrasocial)_.+$/i.test(value)) return true;
  const protocol = /^protocolo_(\d+)$/i.exec(value);
  if (protocol && Number(protocol[1]) > 0) return true;
  if (STORAGE_FILE_TYPE_FOLDER_NAMES.some((type) => type.toLowerCase() === value.toLowerCase())) return true;
  return /^entidad_/i.test(value);
};

/** Entity folders (entidad_…) only hold files: no subfolders can be created inside. */
export const isEntityFolderPath = (path: string | null | undefined): boolean => /^entidad_/i.test(getPathName(path ?? ''));

/**
 * Validation of a new / renamed folder name (mirror of contract §9.2). Returns an i18n key or undefined.
 * `siblingNames` are the folders already shown in the parent (case-insensitive duplicate check).
 */
export const validateFolderName = (
  raw: string | null | undefined,
  siblingNames: string[] = [],
  currentName?: string
): string | undefined => {
  const name = raw ?? '';
  if (!name.trim()) return 'filesExplorer.folderValidation.required';
  if (name !== name.trim()) return 'filesExplorer.folderValidation.edgeSpaces';
  if (name.length > STORAGE_FOLDER_NAME_MAX_LENGTH) return 'filesExplorer.folderValidation.tooLong';
  if (/[\\/]/.test(name) || CONTROL_CHARS.test(name)) return 'filesExplorer.folderValidation.invalidChars';
  if (name === '.' || name === '..') return 'filesExplorer.folderValidation.dots';
  if (isSystemFolderName(name)) return 'filesExplorer.folderValidation.reserved';
  if (currentName !== undefined && name === currentName) return 'filesExplorer.folderValidation.sameName';
  const lower = name.toLowerCase();
  if (
    siblingNames.some(
      (sibling) => sibling.toLowerCase() === lower && (currentName === undefined || sibling !== currentName)
    )
  ) {
    return 'filesExplorer.folderValidation.exists';
  }
  return undefined;
};

/** Normalized allowed extension list (lower case, no dot); null = any except the blocked ones. */
const normalizeExtensions = (extensions: string[] | null | undefined): string[] | null =>
  extensions ? extensions.map((extension) => extension.trim().replace(/^\./, '').toLowerCase()).filter(Boolean) : null;

/**
 * Validation of a file dropped in the explorer (contract §9.2): extension, empty, size, blocked list and,
 * inside entity folders, the extensions of the file type. Returns an i18n key or undefined.
 */
export const validateExplorerUploadFile = (
  file: Pick<File, 'name' | 'size'>,
  allowedExtensions: string[] | null | undefined,
  maxSizeBytes: number = AZURE_MAX_FILE_SIZE_BYTES
): string | undefined => {
  const extension = getExtension(file.name);
  if (!extension) return 'azureFileUpload.validation.noExtension';
  if (file.size === 0) return 'azureFileUpload.validation.emptyFile';
  if (file.size > maxSizeBytes) return 'azureFileUpload.validation.tooLarge';
  if (STORAGE_BLOCKED_EXTENSIONS.includes(extension)) return 'filesExplorer.upload.validation.blocked';
  const allowed = normalizeExtensions(allowedExtensions);
  if (allowed && !allowed.includes(extension)) return 'filesExplorer.upload.validation.notAllowedHere';
  return undefined;
};

/** Value for input[type=file] accept: the folder extensions, or nothing (any) when unrestricted. */
export const buildFolderAcceptAttribute = (allowedExtensions: string[] | null | undefined): string | undefined => {
  const allowed = normalizeExtensions(allowedExtensions);
  return allowed && allowed.length > 0 ? allowed.map((extension) => `.${extension}`).join(',') : undefined;
};

const OWNER_ROOT_SEGMENT = /^(sponsor|practitioner|proveedor|obrasocial)_.+$/i;

/**
 * Path relative to the tenant for a blob path that may include the tenant as first segment
 * (registered blobPath `t1/...` or the legacy upload SAS). Paths that already start with an owner
 * folder or a file-type folder are returned as they are.
 */
export const toTenantRelativePath = (blobPath: string | null | undefined): string => {
  let value = (blobPath ?? '').trim().replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
  try {
    value = decodeURIComponent(value);
  } catch {
    // keep it as it is
  }
  const segments = value.split('/').filter((segment) => segment !== '');
  if (segments.length <= 1) return segments.join('/');
  const first = segments[0];
  if (OWNER_ROOT_SEGMENT.test(first) || STORAGE_FILE_TYPE_FOLDER_NAMES.some((type) => type.toLowerCase() === first.toLowerCase())) {
    return segments.join('/');
  }
  return segments.slice(1).join('/');
};

/** parent + "/" + name ("" parent = root). */
export const joinStoragePath = (parentPath: string, name: string): string =>
  parentPath ? `${parentPath.replace(/\/+$/g, '')}/${name}` : name;

/**
 * After renaming `oldPath` to `newPath`, the folder being shown moves too when it is (or is inside)
 * the renamed one. Returns the new path to show, or undefined when it is not affected.
 */
export const getPathAfterRename = (currentPath: string, oldPath: string, newPath: string): string | undefined => {
  if (!oldPath) return undefined;
  if (currentPath === oldPath) return newPath;
  if (currentPath.startsWith(`${oldPath}/`)) return `${newPath}${currentPath.substring(oldPath.length)}`;
  return undefined;
};

/** Folder of a file (relative to the tenant). */
export const getFileFolder = (path: string): string => getParentPath(path);

/** Link result per file in the picker. */
export type LinkResultStatus = 'linked' | 'alreadyLinked' | 'error';

/** 409 → already linked; anything else → error. */
export const getLinkResultStatus = (error: { ErrorCode?: number } | undefined): LinkResultStatus =>
  !error ? 'linked' : error.ErrorCode === 409 ? 'alreadyLinked' : 'error';

/** Capabilities of the folder being shown; derived from the path while the server has not sent them. */
export interface FolderCapabilities {
  isSystem: boolean;
  canUpload: boolean;
  canCreateFolder: boolean;
  allowedExtensions: string[] | null;
}

export const resolveFolderCapabilities = (
  currentFolder: FolderCapabilities | null | undefined,
  path: string
): FolderCapabilities =>
  currentFolder
    ? {
        isSystem: !!currentFolder.isSystem,
        canUpload: !!currentFolder.canUpload,
        canCreateFolder: !!currentFolder.canCreateFolder,
        allowedExtensions: currentFolder.allowedExtensions ?? null,
      }
    : {
        isSystem: isSystemFolderName(getPathName(path)),
        canUpload: !!path,
        canCreateFolder: !isEntityFolderPath(path),
        allowedExtensions: null,
      };

/** A folder row is a system folder when the server says so, or by its name (§9.1 mirror). */
export const isSystemFolder = (folder: { name: string; isSystem?: boolean }): boolean =>
  folder.isSystem ?? isSystemFolderName(folder.name);

/** Error of a management operation as text: `byStatus` overrides (i18n already applied), then the API message. */
export const describeStorageError = (
  error: { ErrorCode?: number; ErrorMessage?: string; Errors?: string[] } | undefined,
  byStatus: Record<number, string>,
  fallback: string
): string => {
  const code = error?.ErrorCode ?? 0;
  if (byStatus[code]) return byStatus[code];
  const details = (error?.Errors ?? []).map((detail) => String(detail).trim()).filter(Boolean);
  if (details.length > 0) return Array.from(new Set(details)).join(' ');
  return error?.ErrorMessage?.trim() || fallback;
};

// ---- auto-register after an explorer upload (spec 005, US7) ----

/** Owner service register call that matches a destination entity folder. */
export interface FolderRegisterTarget {
  /** Owner service (same kinds as the upload drawer: provider, protocolSponsor, settlement, service...) */
  kind: AzureFileListLoaderKind;
  fileType: AzureFileTypeEnum;
  target: AzureFileTarget;
}

interface FolderRegisterRule {
  fileType: AzureFileTypeEnum;
  entityType: AzureFileEntityTypeEnum;
  /** int: positive integer entity id; guid: entity key in "D" format */
  key: 'int' | 'guid';
  /** Owners allowed for the file type (undefined = any owner) */
  owners?: StorageOwnerTypeEnum[];
  /** The entity lives under sponsor_{id}/protocolo_{id} */
  requiresProtocol?: boolean;
}

/**
 * Entity folders that an owner service registers (blob-path contract §2 + getRegisterKind). ProviderSettlement (1)
 * and ProtocolContract (3) have no register endpoint: files uploaded there are never registered.
 */
const FOLDER_REGISTER_RULES: Record<string, FolderRegisterRule> = {
  sponsorpaymentproposal: {
    fileType: AzureFileTypeEnum.SponsorPaymentProposal,
    entityType: AzureFileEntityTypeEnum.Protocol,
    key: 'int',
    owners: [StorageOwnerTypeEnum.Sponsor],
    requiresProtocol: true,
  },
  providerinvoice: {
    fileType: AzureFileTypeEnum.ProviderInvoice,
    entityType: AzureFileEntityTypeEnum.Provider,
    key: 'int',
    owners: [StorageOwnerTypeEnum.Provider],
  },
  providerservicesperformed: {
    fileType: AzureFileTypeEnum.ProviderServicesPerformed,
    entityType: AzureFileEntityTypeEnum.Provider,
    key: 'int',
    owners: [StorageOwnerTypeEnum.Provider],
  },
  settlementinvoice: {
    fileType: AzureFileTypeEnum.SettlementInvoice,
    entityType: AzureFileEntityTypeEnum.Settlement,
    key: 'int',
  },
  servicereceipt: {
    fileType: AzureFileTypeEnum.ServiceReceipt,
    entityType: AzureFileEntityTypeEnum.Service,
    key: 'guid',
    owners: [StorageOwnerTypeEnum.Sponsor],
    requiresProtocol: true,
  },
  practitionerinvoice: {
    fileType: AzureFileTypeEnum.PractitionerInvoice,
    entityType: AzureFileEntityTypeEnum.Practitioner,
    key: 'guid',
  },
  paymentreceipt: {
    fileType: AzureFileTypeEnum.PaymentReceipt,
    entityType: AzureFileEntityTypeEnum.Payment,
    key: 'int',
  },
};

const OWNER_SEGMENT = /^(sponsor|practitioner|proveedor|obrasocial)_([A-Za-z0-9-]+)$/i;
const OWNER_TYPES: Record<string, StorageOwnerTypeEnum> = {
  sponsor: StorageOwnerTypeEnum.Sponsor,
  practitioner: StorageOwnerTypeEnum.Practitioner,
  proveedor: StorageOwnerTypeEnum.Provider,
  obrasocial: StorageOwnerTypeEnum.MedicalInsurance,
};
const POSITIVE_INT = /^[1-9]\d*$/;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Register target of a destination folder (relative to the tenant) when it is an entity folder of the blob-path
 * contract `{owner}_{id}/[protocolo_{id}/]{FileType}/entidad_{EntityType}_{EntityKey}`, e.g.
 * proveedor_45/ProviderServicesPerformed/entidad_Provider_45 → provider 45. Undefined for any other folder
 * (free folders, owner / type folders, file types without register, mismatching entity type or invalid ids).
 */
export const resolveFolderRegisterTarget = (folderPath: string | null | undefined): FolderRegisterTarget | undefined => {
  const path = normalizeStoragePath(folderPath);
  if (!path) return undefined;
  const segments = path.split('/');
  if (segments.length !== 3 && segments.length !== 4) return undefined;

  const owner = OWNER_SEGMENT.exec(segments[0]);
  if (!owner) return undefined;
  const ownerType = OWNER_TYPES[owner[1].toLowerCase()];

  let protocolId: number | undefined;
  if (segments.length === 4) {
    const protocol = /^protocolo_([1-9]\d*)$/i.exec(segments[1]);
    if (!protocol || ownerType !== StorageOwnerTypeEnum.Sponsor) return undefined;
    protocolId = Number(protocol[1]);
  }

  const rule = FOLDER_REGISTER_RULES[segments[segments.length - 2].toLowerCase()];
  if (!rule) return undefined;
  if (rule.owners && !rule.owners.includes(ownerType)) return undefined;
  if (rule.requiresProtocol && protocolId === undefined) return undefined;

  const entity = /^entidad_([A-Za-z]+)_(.+)$/i.exec(segments[segments.length - 1]);
  if (!entity || entity[1].toLowerCase() !== rule.entityType.toLowerCase()) return undefined;
  const key = entity[2];
  if (rule.key === 'int' ? !POSITIVE_INT.test(key) : !GUID.test(key)) return undefined;

  const target: AzureFileTarget = {
    entityType: rule.entityType,
    entityId: rule.key === 'int' ? Number(key) : 0,
    owner: {
      ownerType,
      ownerId: owner[2],
      ...(protocolId !== undefined ? { protocolId } : {}),
    },
  };
  if (rule.key === 'guid') target.entityKey = key.toLowerCase();
  return { kind: getRegisterKind(rule.fileType), fileType: rule.fileType, target };
};

/** Register target of an uploaded blob (path with or without tenant): the one of its folder. */
export const resolveBlobRegisterTarget = (blobPath: string | null | undefined): FolderRegisterTarget | undefined =>
  resolveFolderRegisterTarget(getParentPath(toTenantRelativePath(blobPath)));
